import { createTheme, alpha, darken } from '@mui/material/styles';

/**
 * Teacher-portal design tokens, transcribed from the HTML mockup
 * (`edumetrix_teacher_themed_responsive.html`).
 *
 * The mockup drives its whole look from CSS custom properties on <html>:
 *   html[data-mode]  -> neutral ramp (--bg/--surface/--ink/--muted/--line/...)
 *   html[data-theme] -> brand pair  (--primary/--secondary/--primary-ink)
 *
 * Those two axes already exist in this app as ThemeContext's `mode` and
 * `colorTheme` (persisted to localStorage, shared by all three portals). This
 * file is the teacher-scoped *resolution* of that state into an MUI theme —
 * the same arrangement `studentTheme.js` uses — so there is still exactly one
 * theme system and one source of user preference. Only the token values are
 * teacher-specific.
 *
 * Note the brand hexes below are the mockup's, which are a shade off the
 * generic Material values in ThemeContext.colorThemes (e.g. blue is #2F74E6,
 * not #1976d2). Keys match `colorThemes` 1:1 so the stored preference, the
 * Settings swatches and the API's `colorTheme` enum all stay compatible.
 */
export const teacherPalettes = {
  blue: { primary: '#2F74E6', secondary: '#E23B5B', primaryInk: '#FFFFFF', name: 'Blue Ocean' },
  purple: { primary: '#9B3FD1', secondary: '#F4A62A', primaryInk: '#FFFFFF', name: 'Purple Haze' },
  green: { primary: '#3F9E48', secondary: '#F2683F', primaryInk: '#FFFFFF', name: 'Nature Green' },
  teal: { primary: '#2E9E8E', secondary: '#E24B72', primaryInk: '#FFFFFF', name: 'Teal Wave' },
  orange: { primary: '#E0921C', secondary: '#4A57C4', primaryInk: '#241800', name: 'Sunset Orange' },
  indigo: { primary: '#5866D6', secondary: '#5FB65A', primaryInk: '#FFFFFF', name: 'Deep Indigo' },
  pink: { primary: '#E24B72', secondary: '#3FB6D6', primaryInk: '#FFFFFF', name: 'Pink Blossom' },
  cyan: { primary: '#1596BC', secondary: '#F4A62A', primaryInk: '#FFFFFF', name: 'Cyan Sky' },
};

// Neutral ramps — mockup `:root` (light) and `html[data-mode="dark"]`.
const neutrals = {
  light: {
    bg: '#FAFAFA',
    surface: '#FFFFFF',
    surface2: '#F4F6FA',
    ink: '#111A2E',
    muted: '#5B6577',
    faint: '#98A2B3',
    line: '#E6E9F0',
    line2: '#F0F2F7',
  },
  dark: {
    bg: '#0F141B',
    surface: '#161C25',
    surface2: '#1C232E',
    ink: '#E7EBF1',
    muted: '#9AA6B4',
    faint: '#66727F',
    line: '#2A333F',
    line2: '#222B35',
  },
};

// Semantic colours — shared by both modes in the mockup.
const semantic = { ok: '#12924A', warn: '#C07C1B', bad: '#E23D3D' };

// Mockup radii: --r (controls) and --r-lg (cards/panels).
const RADIUS = { base: 14, lg: 18 };

export const createTeacherTheme = (mode = 'light', colorTheme = 'blue') => {
  const brand = teacherPalettes[colorTheme] || teacherPalettes.blue;
  const n = neutrals[mode === 'dark' ? 'dark' : 'light'];

  return createTheme({
    palette: {
      mode,
      primary: {
        main: brand.primary,
        // `--primary-strong: color-mix(in srgb, var(--primary) 84%, #000)`
        dark: darken(brand.primary, 0.16),
        light: alpha(brand.primary, 0.13),
        contrastText: brand.primaryInk,
      },
      secondary: { main: brand.secondary, contrastText: '#FFFFFF' },
      success: { main: semantic.ok, contrastText: '#FFFFFF' },
      warning: { main: semantic.warn, contrastText: '#FFFFFF' },
      error: { main: semantic.bad, contrastText: '#FFFFFF' },
      background: { default: n.bg, paper: n.surface },
      text: { primary: n.ink, secondary: n.muted, disabled: n.faint },
      divider: n.line,
      // Mockup-only tokens with no MUI slot. Read via `theme.palette.teacher.*`.
      teacher: {
        surface2: n.surface2,
        line: n.line,
        line2: n.line2,
        faint: n.faint,
        primaryStrong: darken(brand.primary, 0.16),
        primaryInk: brand.primaryInk,
        // `.btn-join` / `.btn-ring` are fixed brand-independent colours.
        join: '#2E7D32',
        joinHover: '#276b2b',
        ring: '#E0731A',
        ringHover: '#c96412',
      },
    },

    // `body{font-size:14px;line-height:1.55}` with the mockup's heading scale.
    typography: {
      fontFamily: '"Inter", system-ui, -apple-system, "Roboto", "Helvetica", sans-serif',
      fontSize: 14,
      htmlFontSize: 16,
      body1: { fontSize: '14px', lineHeight: 1.55 },
      body2: { fontSize: '13px', lineHeight: 1.55 },
      h1: { fontSize: '26px', fontWeight: 700, letterSpacing: '-.01em', lineHeight: 1.2 },
      h2: { fontSize: '24px', fontWeight: 700, letterSpacing: '-.01em', lineHeight: 1.2 },
      h3: { fontSize: '20px', fontWeight: 700, letterSpacing: '-.01em', lineHeight: 1.2 },
      h4: { fontSize: '19px', fontWeight: 700, letterSpacing: '-.01em', lineHeight: 1.2 },
      h5: { fontSize: '16px', fontWeight: 700, lineHeight: 1.25 },
      h6: { fontSize: '15px', fontWeight: 700, lineHeight: 1.25 },
      subtitle1: { fontSize: '14px', fontWeight: 600 },
      subtitle2: { fontSize: '12.5px', fontWeight: 600 },
      button: { fontSize: '13px', fontWeight: 600, textTransform: 'none' },
      caption: { fontSize: '12.5px' },
      overline: { fontSize: '11.5px', fontWeight: 700, letterSpacing: '.04em' },
    },

    shape: { borderRadius: RADIUS.base },

    components: {
      // `.panel` / `.stat` / `.tclass` / `.stud-card` share one card chrome:
      // surface fill, 1px line border, 18px radius, no drop shadow at rest.
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: n.surface,
            border: `1px solid ${n.line}`,
            borderRadius: RADIUS.lg,
            boxShadow: 'none',
            transition: 'border-color .18s, box-shadow .18s',
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: 'none', borderRadius: RADIUS.lg },
        },
      },

      // `.btn` — 10px radius, 9px/15px padding, no MUI elevation.
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 10,
            padding: '9px 15px',
            fontSize: '13px',
            fontWeight: 600,
            textTransform: 'none',
            transition: '.15s',
            gap: '8px',
            '& .MuiButton-startIcon': { marginRight: 0, marginLeft: 0 },
            '& .MuiButton-endIcon': { marginLeft: 0, marginRight: 0 },
          },
          sizeSmall: { padding: '7px 12px', fontSize: '12.5px' },
          // `.btn-p` / `.btn-p:hover`
          contained: {
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          },
          containedPrimary: {
            '&:hover': { backgroundColor: darken(brand.primary, 0.16) },
          },
          // `.btn-o` — surface fill, line border, primary on hover.
          outlined: {
            backgroundColor: n.surface,
            borderColor: n.line,
            color: n.ink,
            '&:hover': {
              borderColor: brand.primary,
              color: brand.primary,
              backgroundColor: n.surface,
            },
          },
        },
      },

      MuiIconButton: {
        styleOverrides: {
          // `.ibtn`
          root: {
            borderRadius: 10,
            color: n.muted,
            transition: '.15s',
            '&:hover': { backgroundColor: n.line2, color: n.ink },
          },
        },
      },

      // `.tag` — 7px radius, 11.5px/700.
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 7,
            fontSize: '11.5px',
            fontWeight: 700,
            height: 'auto',
            padding: '3px 2px',
          },
          label: { paddingLeft: 8, paddingRight: 8 },
          sizeSmall: { fontSize: '11.5px' },
          outlined: { borderColor: n.line },
        },
      },

      // `.utabs` / `.utab`
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 'auto',
            borderBottom: `1px solid ${n.line}`,
          },
          indicator: { height: 2, backgroundColor: brand.primary },
          scrollButtons: { '&.Mui-disabled': { opacity: 0.3 } },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 'auto',
            padding: '12px 16px',
            fontSize: '13.5px',
            fontWeight: 600,
            textTransform: 'none',
            color: n.muted,
            '&:hover': { color: n.ink },
            '&.Mui-selected': { color: brand.primary, fontWeight: 700 },
          },
        },
      },

      // `.att-table`
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: '14px 10px',
            fontSize: '13px',
            borderBottom: `1px solid ${n.line2}`,
          },
          head: {
            fontSize: '12px',
            fontWeight: 700,
            color: n.muted,
            borderBottom: `1px solid ${n.line}`,
          },
        },
      },

      // `.ctrl` / `.note-inp` / `.search input`
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            fontSize: '14px',
            backgroundColor: n.surface,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: n.line },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: n.faint },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: 1,
              borderColor: brand.primary,
            },
          },
          input: { '&::placeholder': { color: n.faint, opacity: 1 } },
        },
      },
      MuiInputLabel: { styleOverrides: { root: { fontSize: '14px' } } },

      // `.modal` — 20px radius, deep shadow.
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 20,
            backgroundImage: 'none',
            backgroundColor: n.surface,
            boxShadow: '0 30px 70px -20px rgba(0,0,0,.45)',
          },
        },
      },
      MuiDialogTitle: { styleOverrides: { root: { padding: '26px 26px 0' } } },
      MuiDialogContent: { styleOverrides: { root: { padding: '6px 26px' } } },
      MuiDialogActions: { styleOverrides: { root: { padding: '20px 26px 26px', gap: 12 } } },

      // `.udrop`
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
            border: `1px solid ${n.line}`,
            boxShadow: '0 22px 55px -18px rgba(17,26,46,.42)',
            padding: 8,
          },
          list: { padding: 0 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            padding: '11px 12px',
            fontSize: '14px',
            fontWeight: 600,
            gap: 12,
            '&:hover': { backgroundColor: n.line2 },
          },
        },
      },

      MuiDivider: { styleOverrides: { root: { borderColor: n.line2 } } },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { fontSize: '12px', borderRadius: 8, backgroundColor: n.ink, color: n.surface },
        },
      },
      MuiAlert: { styleOverrides: { root: { borderRadius: RADIUS.base, fontSize: '13.5px' } } },
      MuiLinearProgress: { styleOverrides: { root: { height: 8, borderRadius: 20 } } },
      MuiCheckbox: { styleOverrides: { root: { padding: 6 } } },
      MuiSelect: { styleOverrides: { select: { fontSize: '14px' } } },
      MuiAvatar: { styleOverrides: { root: { fontWeight: 700 } } },
      MuiPagination: { styleOverrides: { root: { '& .MuiPaginationItem-root': { borderRadius: 10 } } } },

      // `@media(prefers-reduced-motion:reduce){*{animation:none;transition:none}}`
      MuiCssBaseline: {
        styleOverrides: {
          '@media (prefers-reduced-motion: reduce)': {
            '*': { animation: 'none !important', transition: 'none !important' },
          },
        },
      },
    },
  });
};

export default createTeacherTheme;
